import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { handleSnapshotError } from "../utils/recoveryErrors";
import { isSubscriptionGrantingAccess } from "../utils/subscriptionAccess";

export type UserHasSubscriptionCallback = (hasSubscription: boolean) => void;

function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && code.toLowerCase().includes("permission-denied");
}

export function observeUserHasSubscription(
  userId: string,
  callback: UserHasSubscriptionCallback
): () => void {
  const userRef = doc(db, "users", userId);
  let unsubscribeSubscriptions: (() => void) | null = null;

  const unsubscribeUser = onSnapshot(
    userRef,
    (userSnapshot) => {
      if (!userSnapshot.exists()) {
        callback(false);
        if (unsubscribeSubscriptions) {
          unsubscribeSubscriptions();
          unsubscribeSubscriptions = null;
        }
        return;
      }

      const userData = userSnapshot.data();
      const customerId =
        typeof userData.customerId === "string" ? userData.customerId : null;

      if (!customerId) {
        callback(false);
        if (unsubscribeSubscriptions) {
          unsubscribeSubscriptions();
          unsubscribeSubscriptions = null;
        }
        return;
      }

      if (unsubscribeSubscriptions) {
        unsubscribeSubscriptions();
        unsubscribeSubscriptions = null;
      }

      const subscriptionQuery = query(
        collection(db, "subscriptions"),
        where("customerId", "==", customerId),
        limit(10)
      );

      unsubscribeSubscriptions = onSnapshot(
        subscriptionQuery,
        (subscriptionSnapshot) => {
          const hasActiveSubscription = subscriptionSnapshot.docs.some((docSnap) =>
            isSubscriptionGrantingAccess(docSnap.data() ?? {})
          );
          callback(hasActiveSubscription);
        },
        (error) => {
          // Permission-denied is the expected signed-out state — stay silent.
          if (isPermissionDeniedError(error)) {
            callback(false);
            return;
          }
          handleSnapshotError("subscriptions", error, () => callback(false));
        }
      );
    },
    (error) => {
      // Permission-denied is the expected signed-out state — stay silent.
      if (isPermissionDeniedError(error)) {
        callback(false);
        return;
      }
      handleSnapshotError("subscription_user_doc", error, () => callback(false));
    }
  );

  return () => {
    unsubscribeUser();
    if (unsubscribeSubscriptions) {
      unsubscribeSubscriptions();
    }
  };
}
